import React, { useCallback, useState, useMemo } from 'react'
import { useAppSelector } from 'common/hooks/redux'
import { pdf } from '@react-pdf/renderer'
import { DrawingElementVisibleState } from 'features/Procedure/state/procedure.type'
import type { DrawingElement } from 'features/Procedure/state/procedure.type'
import { useAuth } from 'app/providers/auth/AuthProvider'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import * as THREE from 'three'
import type { CanvasCaptureRef } from 'features/Procedure/procedure-model/canvas-capture'
import {
  useFetchEncounterPatientsQuery,
  useFetchProceduresQuery,
  useUploadEncounterPhotoMutation,
  useUpdateProcedureMutation,
} from 'features/Procedure/state/procedure.api'
import { useParams } from 'react-router'
import { findPatientById } from 'features/Procedure/components/patient-encounter-select/patient-encounter-select.utils'
import type {
  Provider,
  PatientWithEncounters,
  PatientEncounter,
  ProcedureItem,
} from 'features/Procedure/state/procedure-api.type'
import { useTimezone } from 'common/hooks/useTimezone'
import { ProcedurePDFDocument } from 'features/Procedure/components/ProcedurePDFDocument'
import { useSceneActions } from 'features/Procedure/hooks/useSceneActions'

type Props = {
  canvasCaptureRef: React.RefObject<CanvasCaptureRef>
  selectedPatientId: number | null
}

export const usePrintDrawings = ({ canvasCaptureRef, selectedPatientId }: Props) => {
  const { setNotification } = useNotification()
  const [isPrinting, setIsPrinting] = useState(false)
  const [progress, setProgress] = useState('')
  const { siteId } = useParams<{ siteId: string }>()
  const { timeZone } = useTimezone()
  const { handleSetShowElementIds, handleSetIsPrinting, handleSetDrawingElements } =
    useSceneActions()
  const showElementIds = useAppSelector((state) => state.procedureReducer.showElementIds)
  const selectedEncounterUid = useAppSelector(
    (state) => state.procedureReducer.selectedEncounterUid,
  )

  const loadLogoAsDataURL = async (): Promise<string> => {
    try {
      const response = await fetch('/logo.svg')
      const svgText = await response.text()

      return new Promise((resolve) => {
        const img = new Image()
        const blob = new Blob([svgText], { type: 'image/svg+xml' })
        const url = URL.createObjectURL(blob)

        img.onload = () => {
          const scale = 4
          const canvas = document.createElement('canvas')
          canvas.width = (img.width || 48) * scale
          canvas.height = (img.height || 48) * scale
          const ctx = canvas.getContext('2d')
          if (ctx) {
            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = 'high'
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
            const dataURL = canvas.toDataURL('image/png', 1.0)
            URL.revokeObjectURL(url)
            resolve(dataURL)
          } else {
            URL.revokeObjectURL(url)
            resolve('')
          }
        }

        img.onerror = () => {
          URL.revokeObjectURL(url)
          resolve('')
        }

        img.src = url
      })
    } catch (error) {
      console.error('Failed to load logo:', error)
      return ''
    }
  }

  const formatDateForFilename = (date: Date): string => {
    const formatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timeZone || undefined,
    })
    const parts = formatter.formatToParts(date)
    const year = parts.find((p) => p.type === 'year')?.value
    const month = parts.find((p) => p.type === 'month')?.value
    const day = parts.find((p) => p.type === 'day')?.value
    return `${year}-${month}-${day}`
  }
  const { emrId } = useAuth()

  const drawingElements = useAppSelector((state) => state.procedureReducer.drawingElements)

  const { data: encounterPatientsData } = useFetchEncounterPatientsQuery(
    { siteId: siteId! },
    {
      skip: !siteId,
    },
  )

  const { data: proceduresData } = useFetchProceduresQuery(
    { siteId: siteId!, encounterUid: selectedEncounterUid! },
    { skip: !siteId || !selectedEncounterUid },
  )

  const [uploadPhoto] = useUploadEncounterPhotoMutation()
  const [updateProcedure] = useUpdateProcedureMutation()

  const proceduresMap = useMemo(() => {
    if (!proceduresData?.items) return new Map<number, ProcedureItem>()
    const map = new Map<number, ProcedureItem>()
    proceduresData.items.forEach((item) => {
      map.set(item.id, item)
    })
    return map
  }, [proceduresData])

  const providerData = useMemo<Provider | null>(() => {
    if (!encounterPatientsData?.providers || !emrId) return null
    return encounterPatientsData.providers.find((p) => p.emr_id === emrId) || null
  }, [encounterPatientsData, emrId])

  const patientData = useMemo<PatientWithEncounters | null>(() => {
    if (!encounterPatientsData?.patients || !selectedPatientId) return null
    return findPatientById(encounterPatientsData.patients, selectedPatientId) || null
  }, [encounterPatientsData, selectedPatientId])

  const encounterData = useMemo<PatientEncounter | null>(() => {
    if (!patientData?.encounters || !selectedEncounterUid) return null
    return patientData.encounters.find((e) => e.encounter_uuid === selectedEncounterUid) || null
  }, [patientData, selectedEncounterUid])

  const visibleElements = drawingElements.filter(
    (el) =>
      el.visibleState === DrawingElementVisibleState.VISIBLE ||
      el.visibleState === DrawingElementVisibleState.PERSISTED,
  )

  const hasElementToSave = drawingElements.some(
    (el) =>
      el.visibleState === DrawingElementVisibleState.VISIBLE ||
      (el.visibleState === DrawingElementVisibleState.PERSISTED && el.isModified),
  )

  const calculateProcedureBounds = (elements: DrawingElement[]) => {
    if (elements.length === 0) return null

    const allPoints: THREE.Vector3[] = []
    elements.forEach((element) => {
      element.points.forEach((point) => {
        allPoints.push(new THREE.Vector3(point.x, point.y, point.z))
      })
    })

    if (allPoints.length === 0) return null

    const elementsWithCamera = elements.filter((el) => el.camera?.position && el.camera?.target)

    if (elementsWithCamera.length > 0) {
      try {
        const avgCameraX =
          elementsWithCamera.reduce((sum, el) => sum + (el.camera?.position.x || 0), 0) /
          elementsWithCamera.length
        const avgCameraY =
          elementsWithCamera.reduce((sum, el) => sum + (el.camera?.position.y || 0), 0) /
          elementsWithCamera.length
        const avgCameraZ =
          elementsWithCamera.reduce((sum, el) => sum + (el.camera?.position.z || 0), 0) /
          elementsWithCamera.length

        const avgTargetX =
          elementsWithCamera.reduce((sum, el) => sum + (el.camera?.target.x || 0), 0) /
          elementsWithCamera.length
        const avgTargetY =
          elementsWithCamera.reduce((sum, el) => sum + (el.camera?.target.y || 0), 0) /
          elementsWithCamera.length
        const avgTargetZ =
          elementsWithCamera.reduce((sum, el) => sum + (el.camera?.target.z || 0), 0) /
          elementsWithCamera.length

        const cameraPosition = new THREE.Vector3(avgCameraX, avgCameraY, avgCameraZ)
        const cameraTarget = new THREE.Vector3(avgTargetX, avgTargetY, avgTargetZ)

        return {
          center: cameraTarget,
          cameraPosition,
          elements,
          isFrontSide: avgTargetZ >= 0,
        }
      } catch (error) {
        console.warn(
          'Error calculating camera bounds from saved coordinates, falling back to default:',
          error,
        )
      }
    }

    const box = new THREE.Box3().setFromPoints(allPoints)
    const size = box.getSize(new THREE.Vector3())

    const maxDimension = Math.max(size.x, size.y, size.z)

    const fov = 40
    const fovRadians = (fov * Math.PI) / 180
    const distanceToFit = maxDimension / (2 * Math.tan(fovRadians / 2))

    const cameraDistance = Math.max(distanceToFit * 2, 9)

    const avgX = allPoints.reduce((sum, p) => sum + p.x, 0) / allPoints.length
    const avgY = allPoints.reduce((sum, p) => sum + p.y, 0) / allPoints.length
    const avgZ = allPoints.reduce((sum, p) => sum + p.z, 0) / allPoints.length

    const cameraTarget = new THREE.Vector3(avgX, avgY, avgZ)
    const cameraPosition = new THREE.Vector3(
      avgX,
      avgY + cameraDistance * 0.2,
      avgZ + cameraDistance,
    )

    return {
      center: cameraTarget,
      cameraPosition,
      elements,
      isFrontSide: avgZ >= 0,
    }
  }

  const captureProcedureZone = async (
    procedureId: string,
    elements: DrawingElement[],
    showOnlyCurrentProcedure: boolean = false,
  ): Promise<string[]> => {
    if (!canvasCaptureRef.current) {
      throw new Error('Canvas capture not available')
    }

    const bounds = calculateProcedureBounds(elements)
    if (!bounds) {
      throw new Error('Could not calculate bounds for procedure')
    }

    if (showOnlyCurrentProcedure) {
      const currentProcedureElements = visibleElements.filter(
        (el) => el.procedureId === parseInt(procedureId),
      )
      handleSetDrawingElements(currentProcedureElements)
      await new Promise((resolve) => requestAnimationFrame(resolve))
    }

    const elementsWithCamera = elements.filter((el) => el.camera?.position && el.camera?.target)
    const hasSavedCameraView = elementsWithCamera.length > 0

    const allPoints: THREE.Vector3[] = []
    elements.forEach((element) => {
      element.points.forEach((point) => {
        allPoints.push(new THREE.Vector3(point.x, point.y, point.z))
      })
    })

    const zValues = allPoints.map((p) => p.z)
    const minZ = Math.min(...zValues)
    const maxZ = Math.max(...zValues)
    const zRange = maxZ - minZ
    const hasFrontElements = zValues.some((z) => z > 0.1)
    const hasBackElements = zValues.some((z) => z < -0.1)
    const needsBothViews =
      !hasSavedCameraView && hasFrontElements && hasBackElements && zRange > 0.5

    const images: string[] = []

    if (needsBothViews) {
      const frontBounds = calculateProcedureBounds(
        elements.filter((el) => el.points.some((p) => p.z > 0)),
      )
      if (frontBounds) {
        await new Promise((resolve) => requestAnimationFrame(resolve))
        const frontImage = await canvasCaptureRef.current.captureOffscreen(
          frontBounds.cameraPosition,
          frontBounds.center,
          `${procedureId}-front`,
          frontBounds.center,
        )
        images.push(frontImage)
        await new Promise((resolve) => requestAnimationFrame(resolve))
      }

      const backBounds = calculateProcedureBounds(
        elements.filter((el) => el.points.some((p) => p.z < 0)),
      )
      if (backBounds) {
        await new Promise((resolve) => requestAnimationFrame(resolve))
        const backImage = await canvasCaptureRef.current.captureOffscreen(
          backBounds.cameraPosition,
          backBounds.center,
          `${procedureId}-back`,
          backBounds.center,
        )
        images.push(backImage)
      }
    } else {
      await new Promise((resolve) => requestAnimationFrame(resolve))
      const imageData = await canvasCaptureRef.current.captureOffscreen(
        bounds.cameraPosition,
        bounds.center,
        procedureId,
        bounds.center,
      )
      images.push(imageData)
    }

    if (showOnlyCurrentProcedure) {
      handleSetDrawingElements(visibleElements)
      await new Promise((resolve) => requestAnimationFrame(resolve))
    }

    return images
  }

  const generatePDF = async (
    images: { procedureId: number; imageData: string; elements: DrawingElement[] }[],
    providerData: Provider | null,
    patientData: PatientWithEncounters | null,
    encounterData: PatientEncounter | null,
    proceduresMap: Map<number, ProcedureItem>,
  ) => {
    const logoDataURL = await loadLogoAsDataURL()

    const document = React.createElement(ProcedurePDFDocument, {
      images,
      providerData,
      patientData,
      encounterData,
      proceduresMap,
      logoDataURL,
      timeZone,
    }) as React.ReactElement

    const blob = await pdf(document).toBlob()
    return blob
  }

  const handlePrintPdf = useCallback(
    async ({
      downloadLocally = false,
      showOnlyCurrentProcedure = false,
    }: {
      downloadLocally: boolean
      showOnlyCurrentProcedure: boolean
    }) => {
      if (!proceduresData?.items || proceduresData.items.length === 0) {
        return
      }

      setIsPrinting(true)
      setProgress('Initializing...')

      const originalShowElementIds = showElementIds
      handleSetShowElementIds(true)
      handleSetIsPrinting(true)

      await new Promise((resolve) => setTimeout(resolve, 200))

      try {
        const procedureGroups = new Map<number, DrawingElement[]>()

        visibleElements.forEach((element) => {
          const key = element.procedureId
          const existing = procedureGroups.get(key) || []
          procedureGroups.set(key, [...existing, element])
        })

        const allProcedureIds = proceduresData.items.map((item) => item.id)
        const images: {
          procedureId: number
          imageData: string
          elements: DrawingElement[]
        }[] = []

        for (let i = 0; i < allProcedureIds.length; i++) {
          const procedureId = allProcedureIds[i]
          const elements = procedureGroups.get(procedureId) || []
          setProgress(
            `Capturing procedure ${i + 1}/${allProcedureIds.length} (${
              elements.length
            } elements)...`,
          )

          await new Promise((resolve) => requestAnimationFrame(resolve))

          if (elements.length > 0) {
            try {
              const capturedImages = await captureProcedureZone(
                procedureId.toString(),
                elements,
                showOnlyCurrentProcedure,
              )
              capturedImages.forEach((imageData) => {
                images.push({
                  procedureId,
                  imageData,
                  elements,
                })
              })
            } catch (error) {
              console.error(`Failed to capture procedure ${procedureId}:`, error)
            }
          } else {
            images.push({
              procedureId,
              imageData: '',
              elements: [],
            })
          }

          await new Promise((resolve) => requestAnimationFrame(resolve))
        }

        setProgress('Generating PDF...')

        await new Promise((resolve) => requestAnimationFrame(resolve))

        const pdfBlob = await generatePDF(
          images,
          providerData,
          patientData,
          encounterData,
          proceduresMap,
        )

        const fileName = `procedure-drawings-${formatDateForFilename(new Date())}.pdf`

        if (downloadLocally) {
          const url = URL.createObjectURL(pdfBlob)
          const link = document.createElement('a')
          link.href = url
          link.download = fileName
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        }

        setProgress('Uploading PDF to server...')

        if (siteId && selectedPatientId && selectedEncounterUid) {
          try {
            const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' })
            const formData = new FormData()
            formData.append('photo', pdfFile)
            formData.append('file_name', fileName)

            await uploadPhoto({
              siteId,
              patientId: selectedPatientId,
              encounterUid: selectedEncounterUid,
              formData,
            }).unwrap()
          } catch (uploadError) {
            console.error('Failed to upload PDF to server:', uploadError)
          }
        }
      } catch (error) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to generate PDF',
        })
      } finally {
        handleSetShowElementIds(originalShowElementIds)
        handleSetIsPrinting(false)
        setIsPrinting(false)
        setProgress('')
      }
    },
    [
      visibleElements,
      setNotification,
      hasElementToSave,
      canvasCaptureRef,
      proceduresData,
      providerData,
      patientData,
      encounterData,
      proceduresMap,
      showElementIds,
      handleSetShowElementIds,
      handleSetIsPrinting,
      handleSetDrawingElements,
      siteId,
      selectedPatientId,
      selectedEncounterUid,
      uploadPhoto,
    ],
  )

  const handleSaveImages = useCallback(
    async ({
      downloadLocally = false,
      showOnlyCurrentProcedure = false,
    }: {
      downloadLocally: boolean
      showOnlyCurrentProcedure: boolean
    }) => {
      if (!proceduresData?.items || proceduresData.items.length === 0) {
        return
      }

      setIsPrinting(true)
      setProgress('Initializing...')

      const originalShowElementIds = showElementIds
      handleSetShowElementIds(true)
      handleSetIsPrinting(true)

      await new Promise((resolve) => setTimeout(resolve, 200))

      try {
        const procedureGroups = new Map<number, DrawingElement[]>()

        visibleElements.forEach((element) => {
          const key = element.procedureId
          const existing = procedureGroups.get(key) || []
          procedureGroups.set(key, [...existing, element])
        })

        const allProcedureIds = proceduresData.items.map((item) => item.id)

        for (let i = 0; i < allProcedureIds.length; i++) {
          const procedureId = allProcedureIds[i]
          const elements = procedureGroups.get(procedureId) || []

          if (elements.length === 0) continue

          setProgress(
            `Capturing procedure ${i + 1}/${allProcedureIds.length} (${
              elements.length
            } elements)...`,
          )

          await new Promise((resolve) => requestAnimationFrame(resolve))

          try {
            const capturedImages = await captureProcedureZone(
              procedureId.toString(),
              elements,
              showOnlyCurrentProcedure,
            )

            if (capturedImages.length > 0) {
              const imageData = capturedImages[0]
              const timestamp = formatDateForFilename(new Date())
              const imageName = `procedure-${procedureId}-${timestamp}.png`

              if (downloadLocally) {
                const blob = await fetch(imageData).then((r) => r.blob())
                const url = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = imageName
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(url)
              }

              if (siteId && selectedEncounterUid && proceduresData?.model_version) {
                setProgress(`Uploading image for procedure ${i + 1}/${allProcedureIds.length}...`)

                try {
                  const procedureItem = proceduresData.items.find((p) => p.id === procedureId)
                  if (!procedureItem) {
                    console.error(`Procedure ${procedureId} not found`)
                    continue
                  }

                  await updateProcedure({
                    siteId,
                    encounterUid: selectedEncounterUid,
                    data: {
                      model_version: proceduresData.model_version,
                      procedures: [
                        {
                          id: procedureId,
                          code: procedureItem.code,
                          code_type: procedureItem.code_type,
                          procedure_units: procedureItem.procedure_units,
                          units: procedureItem.units,
                          fee: procedureItem.fee,
                          provider_id: procedureItem.provider_id,
                          justify_codes: procedureItem.justify_codes,
                          image: imageData,
                        },
                      ],
                    },
                  }).unwrap()
                } catch (uploadError) {
                  console.error(`Failed to upload image for procedure ${procedureId}:`, uploadError)
                }
              }
            }
          } catch (error) {
            console.error(`Failed to capture procedure ${procedureId}:`, error)
          }

          await new Promise((resolve) => requestAnimationFrame(resolve))
        }
      } catch (error) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to save images',
        })
      } finally {
        handleSetShowElementIds(originalShowElementIds)
        handleSetIsPrinting(false)
        setIsPrinting(false)
        setProgress('')
      }
    },
    [
      visibleElements,
      setNotification,
      hasElementToSave,
      canvasCaptureRef,
      proceduresData,
      showElementIds,
      handleSetShowElementIds,
      handleSetIsPrinting,
      handleSetDrawingElements,
      siteId,
      selectedEncounterUid,
      updateProcedure,
    ],
  )

  const hasProcedures = useMemo(
    () => proceduresData?.items && proceduresData.items.length > 0,
    [proceduresData],
  )

  return {
    handlePrintPdf,
    handleSaveImages,
    isPrinting,
    progress,
    hasElementsToPrint: true,
    hasProcedures,
  }
}
